import { Card, Form, FormButtonRow, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils/validation';
import { AuthorizationErrorCode, ManagerAuthorization, ValidationErrorCode } from '@common/enums/';
import { BankAccountEntity } from '@common/enums/bankAccountEntity';
import { getNestedTranslation } from '@common/translate/translate';
import { getDisplayName } from '@common/utils/getDisplayName';
import { employmentPaymentDetailsSchema } from '@common/validation/employment';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { IEmployment } from '../../interfaces/employment';
import { FormContextProvider } from '../../elements/formContext';
import { CancelButton } from '../../userDataManagement/CancelButton';
import { InputTextField } from '../../userDataManagement/InputText';
import { SaveButton } from '../../userDataManagement/SaveButton';
import { BankAccountDetails } from '../bankAccountDetails/BankAccountDetails';
import { BankAccountDetailsLogs } from '../bankAccountDetails/BankAccountDetailsLogs';

interface IPaymentDetailsProps {
    employmentId: string;
    isCurrentUser?: boolean;
    isReadOnly: boolean;
}

export function PaymentDetails(props: IPaymentDetailsProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [employment, setEmployment] = useState<IEmployment>();
    const [originalEmployment, setOriginalEmployment] = useState<IEmployment>();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const canEditFinancialData = hasOneAuthorization(ManagerAuthorization.employmentFinanceData);

    if (isManager) {
        assertHasAuthorization([ManagerAuthorization.employmentSettingsRead]);
    } else if (!props.isCurrentUser) {
        throw new Error(AuthorizationErrorCode.CANNOT_VIEW_OTHER_USERS_EMPLOYMENT_SETTINGS);
    }

    useEffect(() => {
        loadEmployment(props.employmentId);
    }, [props.employmentId]);

    if (!employment) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                errors: errors,
                item: employment,
                onChange: (key, value) => handleChange(key, value),
                onDoValidate: (key) => validateOne(key),
                placeholderGroupName: 'paymentSettings',
            }}
        >
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !props.isReadOnly && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={getSelectedLanguage().common.financialData}
                />
                <Form submitOnEnter={false}>
                    <Card.Body>
                        {renderTextInput(
                            'relationNumber',
                            'employment.relationNumber',
                            !hasOneAuthorization(ManagerAuthorization.employmentRelationNumberEdit),
                            employment.relationNumber
                        )}
                        {renderTextInput(
                            'emailAddressPayouts',
                            'employment.emailAddressPayouts',
                            !hasOneAuthorization(ManagerAuthorization.employmentRelationNumberEdit) &&
                                !hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit),
                            employment.emailAddressPayouts
                        )}

                        {(props.isCurrentUser || canEditFinancialData) && (
                            <BankAccountDetails
                                bankAccountEntity={BankAccountEntity.employment}
                                canProcessBankAccountChanges={canEditFinancialData}
                                canRequestChange={props.isCurrentUser ?? false}
                                entityDisplayName={
                                    employment.practitioner ? getDisplayName(employment.practitioner) : undefined
                                }
                                entityId={employment.id}
                            />
                        )}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => handleCancel()}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Card.Body>
                    {!isEditing && canEditFinancialData && (
                        <BankAccountDetailsLogs
                            bankAccountEntity={BankAccountEntity.employment}
                            entityId={props.employmentId}
                        />
                    )}
                </Form>
            </Card>
        </FormContextProvider>
    );

    function renderTextInput(fieldKey: string, label: string, readOnly: boolean, value?: string | null) {
        const formParagraphLabel = getNestedTranslation(label);

        if (isEditing && !readOnly) {
            return (
                <InputTextField
                    dontValidateOnBlur
                    error={errors.get(fieldKey)}
                    fieldKey={fieldKey}
                    hasErrors={errors.has(fieldKey)}
                    label={label}
                    nullable
                    value={value}
                />
            );
        }

        return <FormParagraphFormGroup label={formParagraphLabel}>{value || '-'}</FormParagraphFormGroup>;
    }

    async function loadEmployment(id: string) {
        const url = `/api/employments/${id}?includePractitioner=true`;

        const result = await fetchJson(url, 'GET');
        setOriginalEmployment(result.json);
        setEmployment(result.json);
    }

    function handleChange<K extends keyof IEmployment>(name: K, value: IEmployment[K]) {
        setEmployment({ ...employment, [name]: value } as IEmployment);
    }

    function handleCancel() {
        setEmployment(originalEmployment);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    function validateOne(name: keyof IEmployment) {
        setErrors(updateValidationErrorsForOne(employmentPaymentDetailsSchema, employment, errors, name));
    }

    async function handleSubmit() {
        if (employment) {
            setIsSaving(true);

            const { errors: validationErrors, item } = validate(employmentPaymentDetailsSchema, employment);

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                const url = `/api/employments/${employment.id}/paymentDetails`;
                const result = await fetchJson(url, 'POST', item, [ValidationErrorCode.RELATION_NUMBER_EXISTS]);

                if (result?.errorCode === ValidationErrorCode.RELATION_NUMBER_EXISTS) {
                    setErrors(emptyValidationErrors.set('relationNumber', ValidationErrorCode.RELATION_NUMBER_EXISTS));
                } else {
                    setErrors(emptyValidationErrors);
                    setOriginalEmployment(employment);
                    setIsEditing(false);
                }
            }
            setIsSaving(false);
        }
    }
}
